import {useState, useCallback} from "react"

export type GuestInputValues = Record<string, string>

interface UseBoothSessionResult {
  rawImage: string | null
  setRawImage: (image: string | null) => void
  aiProcessedImage: string | null
  setAiProcessedImage: (image: string | null) => void
  selectedAiImageIndex: number
  aiImageHistory: string[]
  compositeImage: string | null
  setCompositeImage: (image: string | null) => void
  addAiImage: (image: string) => void
  selectAiImage: (index: number) => void
  guestInputValues: GuestInputValues
  setGuestInputValues: (values: GuestInputValues) => void
  clearImages: () => void
}

export function useBoothSession(presetCode: string): UseBoothSessionResult {
  const [rawImage, setRawImageState] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(`${presetCode}_rawImage`)
    } catch {
      return null
    }
  })

  const [aiProcessedImage, setAiProcessedImageState] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(`${presetCode}_aiProcessedImage`)
    } catch {
      return null
    }
  })

  const [compositeImage, setCompositeImageState] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(`${presetCode}_compositeImage`)
    } catch {
      return null
    }
  })

  const [aiImageHistory, setAiImageHistoryState] = useState<string[]>(() => {
    try {
      const stored = window.localStorage.getItem(`${presetCode}_aiImageHistory`)
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  })

  const [selectedAiImageIndex, setSelectedAiImageIndexState] = useState<number>(() => {
    try {
      const stored = window.localStorage.getItem(`${presetCode}_selectedAiImageIndex`)
      return stored ? parseInt(stored, 10) : 0
    } catch {
      return 0
    }
  })

  const [guestInputValues, setGuestInputValuesState] = useState<GuestInputValues>(() => {
    try {
      const stored = window.localStorage.getItem(`${presetCode}_guestInputValues`)
      return stored ? JSON.parse(stored) : {}
    } catch {
      return {}
    }
  })

  const setRawImage = useCallback((image: string | null) => {
    if (image) {
      try {
        window.localStorage.setItem(`${presetCode}_rawImage`, image)
      } catch {}
    } else {
      try {
        window.localStorage.removeItem(`${presetCode}_rawImage`)
      } catch {}
    }
    setRawImageState(image)
  }, [presetCode])

  const setAiProcessedImage = useCallback((image: string | null) => {
    if (image) {
      try {
        window.localStorage.setItem(`${presetCode}_aiProcessedImage`, image)
      } catch {}
    } else {
      try {
        window.localStorage.removeItem(`${presetCode}_aiProcessedImage`)
      } catch {}
    }
    setAiProcessedImageState(image)
  }, [presetCode])

  const setCompositeImage = useCallback((image: string | null) => {
    if (image) {
      try {
        window.localStorage.setItem(`${presetCode}_compositeImage`, image)
      } catch {}
    } else {
      try {
        window.localStorage.removeItem(`${presetCode}_compositeImage`)
      } catch {}
    }
    setCompositeImageState(image)
  }, [presetCode])

  const addAiImage = useCallback((image: string) => {
    setAiImageHistoryState(prev => {
      const updated = [...prev, image]
      try {
        window.localStorage.setItem(`${presetCode}_aiImageHistory`, JSON.stringify(updated))
      } catch {}
      const newIndex = updated.length - 1
      setSelectedAiImageIndexState(newIndex)
      try {
        window.localStorage.setItem(`${presetCode}_selectedAiImageIndex`, String(newIndex))
      } catch {}
      return updated
    })
    setAiProcessedImage(image)
  }, [presetCode, setAiProcessedImage])

  const selectAiImage = useCallback((index: number) => {
    setAiImageHistoryState(history => {
      if (index >= 0 && index < history.length) {
        setSelectedAiImageIndexState(index)
        try {
          window.localStorage.setItem(`${presetCode}_selectedAiImageIndex`, String(index))
        } catch {}
        setAiProcessedImage(history[index])
      }
      return history
    })
  }, [presetCode, setAiProcessedImage])

  const setGuestInputValues = useCallback((values: GuestInputValues) => {
    setGuestInputValuesState(values)
    try {
      window.localStorage.setItem(`${presetCode}_guestInputValues`, JSON.stringify(values))
    } catch {}
  }, [presetCode])

  const clearImages = useCallback(() => {
    setRawImage(null)
    setAiProcessedImage(null)
    setCompositeImage(null)
    setAiImageHistoryState([])
    setSelectedAiImageIndexState(0)
    setGuestInputValues({})
    try {
      window.localStorage.removeItem(`${presetCode}_aiImageHistory`)
      window.localStorage.removeItem(`${presetCode}_selectedAiImageIndex`)
    } catch {}
  }, [presetCode, setRawImage, setAiProcessedImage, setCompositeImage, setGuestInputValues])

  return {
    rawImage,
    aiProcessedImage,
    compositeImage,
    aiImageHistory,
    selectedAiImageIndex,
    guestInputValues,
    setRawImage,
    setAiProcessedImage,
    setCompositeImage,
    addAiImage,
    selectAiImage,
    setGuestInputValues,
    clearImages
  }
}
